import { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  FolderKanban,
  Inbox,
  LayoutDashboard,
  Mail,
  Search,
  Send,
  Settings,
  Sparkles,
  Users,
  X,
} from "lucide-react";

export type SearchTarget = {
  id: string;
  kind: string;
  title: string;
  subtitle: string;
  view: string;
  projectId?: string;
};

type ProjectOption = { id: string; name: string; slug: string };

const sections = [
  {
    view: "Обзор",
    keywords: "главная сводка dashboard",
    icon: LayoutDashboard,
  },
  { view: "Gmail", keywords: "почта ящики google oauth", icon: Mail },
  { view: "База лидов", keywords: "лиды база импорт csv таблица", icon: Users },
  {
    view: "Гипотезы",
    keywords: "гипотезы playbook правила этапы",
    icon: Sparkles,
  },
  { view: "Рассылки", keywords: "рассылки подборки письма", icon: Send },
  { view: "Входящие", keywords: "входящие ответы диалоги", icon: Inbox },
  { view: "Аналитика", keywords: "аналитика метрики воронка", icon: BarChart3 },
  {
    view: "Интеграции",
    keywords: "интеграции telegram openai",
    icon: Settings,
  },
  { view: "Настройки", keywords: "настройки профиль аккаунт", icon: Settings },
] as const;

const kindLabels: Record<string, string> = {
  gmail: "Ящик",
  lead: "Лид",
  hypothesis: "Гипотеза",
  campaign: "Кампания",
  batch: "Батч",
  import: "Импорт",
  inbox: "Письмо",
};
const kindIcons: Record<string, typeof Search> = {
  gmail: Mail,
  lead: Users,
  hypothesis: Sparkles,
  campaign: Send,
  batch: Send,
  import: Users,
  inbox: Inbox,
  project: FolderKanban,
  section: LayoutDashboard,
};

export function GlobalSearch({
  projectId,
  projects,
  onSelect,
}: {
  projectId: string;
  projects: ProjectOption[];
  onSelect: (target: SearchTarget) => void;
}) {
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState<SearchTarget[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const normalized = query.trim().toLocaleLowerCase("ru-RU");
  const sectionResults = useMemo<SearchTarget[]>(
    () =>
      normalized
        ? sections
            .filter((item) =>
              `${item.view} ${item.keywords}`
                .toLocaleLowerCase("ru-RU")
                .includes(normalized),
            )
            .map((item) => ({
              id: `section:${item.view}`,
              kind: "section",
              title: item.view,
              subtitle: "Раздел",
              view: item.view,
            }))
        : [],
    [normalized],
  );
  const projectResults = useMemo<SearchTarget[]>(
    () =>
      normalized
        ? projects
            .filter((item) =>
              `${item.name} ${item.slug}`
                .toLocaleLowerCase("ru-RU")
                .includes(normalized),
            )
            .map((item) => ({
              id: `project:${item.id}`,
              kind: "project",
              title: item.name,
              subtitle: "Проект",
              view: "Обзор",
              projectId: item.id,
            }))
        : [],
    [normalized, projects],
  );
  const results = useMemo(
    () => [...sectionResults, ...projectResults, ...remote].slice(0, 18),
    [sectionResults, projectResults, remote],
  );

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLocaleLowerCase() === "k"
      ) {
        event.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);

  useEffect(() => {
    setActive(0);
    if (normalized.length < 2 || !projectId) {
      setRemote([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(
      () =>
        fetch(
          `/api/projects/${projectId}/search?q=${encodeURIComponent(query.trim())}`,
          { signal: controller.signal },
        )
          .then(async (response) =>
            response.ok
              ? (response.json() as Promise<{ results: SearchTarget[] }>)
              : { results: [] },
          )
          .then((result) => setRemote(result.results))
          .catch(() => {
            if (!controller.signal.aborted) setRemote([]);
          })
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          }),
      180,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [normalized, projectId, query]);

  const choose = (target: SearchTarget) => {
    onSelect(target);
    setOpen(false);
    setQuery("");
    setRemote([]);
  };
  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((value) =>
        Math.min(value + 1, Math.max(results.length - 1, 0)),
      );
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((value) => Math.max(value - 1, 0));
    }
    if (event.key === "Enter" && results[active]) {
      event.preventDefault();
      choose(results[active]);
    }
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };
  const groups = [
    { label: "Разделы", items: sectionResults },
    { label: "Проекты", items: projectResults },
    { label: "Данные проекта", items: remote },
  ].filter((group) => group.items.length);
  let itemIndex = -1;
  return (
    <div
      className="global-search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node))
          setOpen(false);
      }}
    >
      <div className={open ? "search active" : "search"}>
        <Search size={17} />
        <input
          ref={inputRef}
          value={query}
          placeholder="Поиск"
          aria-label="Поиск"
          role="combobox"
          aria-expanded={open && Boolean(normalized)}
          aria-controls="global-search-results"
          aria-autocomplete="list"
          onFocus={() => setOpen(Boolean(normalized))}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(Boolean(event.target.value.trim()));
          }}
          onKeyDown={keyDown}
        />
        {query ? (
          <button
            type="button"
            aria-label="Очистить поиск"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setQuery("");
              setRemote([]);
              setOpen(false);
              inputRef.current?.focus();
            }}
          >
            <X size={14} />
          </button>
        ) : (
          <kbd>⌘ K</kbd>
        )}
      </div>
      {open && normalized && (
        <div
          className="search-popover"
          id="global-search-results"
          role="listbox"
        >
          {groups.map((group) => (
            <section key={group.label}>
              <span>{group.label}</span>
              {group.items.map((item) => {
                itemIndex += 1;
                const index = itemIndex;
                const Icon =
                  item.kind === "section"
                    ? (sections.find((section) => section.view === item.view)
                        ?.icon ?? LayoutDashboard)
                    : (kindIcons[item.kind] ?? Search);
                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={active === index}
                    className={active === index ? "active" : ""}
                    key={`${item.kind}:${item.id}`}
                    onMouseEnter={() => setActive(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(item)}
                  >
                    <i>
                      <Icon size={16} />
                    </i>
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.subtitle}</small>
                    </span>
                    <em>
                      {item.kind === "section" || item.kind === "project"
                        ? "Открыть"
                        : (kindLabels[item.kind] ?? "Данные")}
                    </em>
                  </button>
                );
              })}
            </section>
          ))}
          {loading && <div className="search-state">Ищем…</div>}
          {!loading && !results.length && (
            <div className="search-state">Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  );
}
